import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon, SparklesIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { abacPolicyService } from '@/services/abac';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

interface PolicyCondition {
  attribute_path: string;
  operator: string;
  value: any;
  logical_operator: 'AND' | 'OR' | 'NOT';
}

interface PolicyForm {
  name: string;
  description: string;
  effect: 'ALLOW' | 'DENY';
  status: 'ACTIVE' | 'INACTIVE' | 'DRAFT';
  priority: number;
  version: string;
  tags: string[];
  compliance_frameworks: string[];
  conditions: PolicyCondition[];
  actions: string[]; // Actions this policy applies to
}

export const PolicyCreatePage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableAttributes, setAvailableAttributes] = useState<any[]>([]);
  const [availableActions, setAvailableActions] = useState<any[]>([]);
  const [naturalLanguagePolicy, setNaturalLanguagePolicy] = useState('');
  const [showNaturalLanguage, setShowNaturalLanguage] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);

  const [form, setForm] = useState<PolicyForm>({
    name: '',
    description: '',
    effect: 'ALLOW',
    status: 'DRAFT',
    priority: 1,
    version: '1.0.0',
    tags: [],
    compliance_frameworks: [],
    conditions: [],
    actions: []
  });

  const [newTag, setNewTag] = useState('');
  const [newFramework, setNewFramework] = useState('');

  // Group actions by navigation bar structure
  const groupActions = (actions: any[]) => {
    const groupedActions = actions.reduce((groups: any[], action: any) => {
      // Map action names to navigation bar groupings
      let navigationGroup = t('abac.policyCreate.general');
      
      const actionName = action.action_name.toLowerCase();
      
      // Tenant Administration
      if (actionName.includes('tenant') || actionName.includes('company') || actionName.includes('organization')) {
        navigationGroup = t('abac.policyCreate.tenantAdministration');
      } else if (actionName.includes('user') || actionName.includes('profile') || actionName.includes('account') || actionName.includes('role') || actionName.includes('group') || actionName.includes('permission')) {
        navigationGroup = t('abac.policyCreate.companyAdministration');
      } else if (actionName.includes('transformation') || actionName.includes('change') || actionName.includes('initiative') || actionName.includes('portfolio') || actionName.includes('program') || actionName.includes('stakeholder') || actionName.includes('risk') || actionName.includes('budget') || actionName.includes('fte') || actionName.includes('quality') || actionName.includes('lesson')) {
        navigationGroup = t('abac.policyCreate.transformationManagement');
      } else if (actionName.includes('process') || actionName.includes('workflow') || actionName.includes('hierarchy') || actionName.includes('ownership') || actionName.includes('integration')) {
        navigationGroup = t('abac.policyCreate.processModeling');
      } else if (actionName.includes('kpi') || actionName.includes('metric') || actionName.includes('performance') || actionName.includes('target') || actionName.includes('measurement') || actionName.includes('analytics')) {
        navigationGroup = t('abac.policyCreate.performanceKPIManagement');
      } else if (actionName.includes('data') || actionName.includes('integration') || actionName.includes('api') || actionName.includes('synchronization') || actionName.includes('import') || actionName.includes('export') || actionName.includes('quality') || actionName.includes('lineage') || actionName.includes('master') || actionName.includes('reference')) {
        navigationGroup = t('abac.policyCreate.dataConnection');
      } else if (actionName.includes('system') || actionName.includes('config') || actionName.includes('setting') || actionName.includes('monitoring') || actionName.includes('tuning') || actionName.includes('backup') || actionName.includes('audit') || actionName.includes('platform')) {
        navigationGroup = t('abac.policyCreate.systemAdministration');
      } else if (actionName.includes('partner') || actionName.includes('invitation') || actionName.includes('directory')) {
        navigationGroup = t('abac.policyCreate.partnerManagement');
      } else if (actionName.includes('template') || actionName.includes('policy') || actionName.includes('role') || actionName.includes('best') || actionName.includes('practice')) {
        navigationGroup = t('abac.policyCreate.templatesBestPractices');
      } else if (actionName.includes('hierarchy') || actionName.includes('business') || actionName.includes('functional') || actionName.includes('geographic') || actionName.includes('organizational')) {
        navigationGroup = t('abac.policyCreate.organizationalSetup');
      } else if (actionName.includes('report') || actionName.includes('dashboard') || actionName.includes('analytics') || actionName.includes('session') || actionName.includes('audit')) {
        navigationGroup = t('abac.policyCreate.reportingAnalytics');
      }
      
      const existingGroup = groups.find(group => group.name === navigationGroup);
      
      if (existingGroup) {
        existingGroup.actions.push(action);
      } else {
        groups.push({
          name: navigationGroup,
          actions: [action]
        });
      }
      
      return groups;
    }, []);

    return groupedActions.sort((a, b) => a.name.localeCompare(b.name));
  };

  // Group attributes by their group field
  const groupAttributes = (attributes: any[]) => {
    const groupedAttributes = attributes.reduce((groups: any[], attribute: any) => {
      const group = attribute.attribute_group || t('abac.policyCreate.general');
      const existingGroup = groups.find(g => g.name === group);
      
      if (existingGroup) {
        existingGroup.attributes.push(attribute);
      } else {
        groups.push({
          name: group,
          attributes: [attribute]
        });
      }
      
      return groups;
    }, []);

    return groupedAttributes.sort((a, b) => a.name.localeCompare(b.name));
  };

  useEffect(() => {
    fetchAvailableData();
  }, []);

  const fetchAvailableData = async () => {
    try {
      setLoading(true);
            
      // Use the authenticated API service instead of direct fetch
            const [attributesResponse, actionsResponse] = await Promise.all([
        abacPolicyService.getCustomAttributes(),
        abacPolicyService.getActions()
      ]);

            
      if (attributesResponse.data && attributesResponse.data.length > 0) {
                // Transform the custom attributes data to match the expected format
        const transformedAttributes = attributesResponse.data.map((attr: any) => ({
          id: attr.id,
          attribute_path: `${attr.attribute_type.toLowerCase()}.${attr.attribute_name}`,
          attribute_name: `${attr.attribute_type}: ${attr.attribute_name}`,
          attribute_type: attr.attribute_type,
          data_type: attr.data_type
        }));
                setAvailableAttributes(transformedAttributes);
      } else {
                setAvailableAttributes([]);
      }

      if (actionsResponse.data && actionsResponse.data.length > 0) {
                        setAvailableActions(actionsResponse.data);
      } else {
                setAvailableActions([]);
      }
      
          } catch (err: any) {
      console.error('❌ Error fetching available data:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response?.data
      });
    } finally {
      setLoading(false);
    }
  };

  // Natural language policy parser
  const parseNaturalLanguagePolicy = (text: string) => {
    const conditions: PolicyCondition[] = [];
    const actions: string[] = [];
    
    // Extract actions from the text
    const actionPatterns = [
      /action\s*=\s*(\w+)/gi,
      /allow\s+action\s*=\s*(\w+)/gi,
      /deny\s+action\s*=\s*(\w+)/gi,
      /then\s+allow\s+(\w+)/gi,
      /then\s+deny\s+(\w+)/gi
    ];
    
    actionPatterns.forEach(pattern => {
      const matches = text.matchAll(pattern);
      for (const match of matches) {
        const action = match[1]?.trim();
        if (action && !actions.includes(action)) {
          actions.push(action);
        }
      }
    });
    
    // Split by common conjunctions
    const clauses = text.split(/\s+(and|or|but)\s+/i);
    
    clauses.forEach((clause, index) => {
      if (!clause.trim()) return;
      
      // Extract attribute path, operator, and value
      const patterns = [
        // user.attribute = value
        /(user\.\w+)\s*(=|equals|is)\s*(.+)/i,
        // resource.attribute = value  
        /(resource\.\w+)\s*(=|equals|is)\s*(.+)/i,
        // environment.attribute = value
        /(environment\.\w+)\s*(=|equals|is)\s*(.+)/i,
        // attribute < value
        /(\w+\.\w+)\s*(<|less than|smaller than)\s*(.+)/i,
        // attribute > value
        /(\w+\.\w+)\s*(>|greater than|larger than)\s*(.+)/i,
        // attribute contains value
        /(\w+\.\w+)\s*(contains|includes)\s*(.+)/i,
        // attribute != value
        /(\w+\.\w+)\s*(!=|not equals|is not)\s*(.+)/i
      ];
      
      for (const pattern of patterns) {
        const match = clause.match(pattern);
        if (match) {
          const [, attributePath, operator, value] = match;
          
          // Normalize operator
          let normalizedOperator = 'EQUALS';
          if (['<', 'less than', 'smaller than'].includes(operator.toLowerCase())) {
            normalizedOperator = 'LESS_THAN';
          } else if (['>', 'greater than', 'larger than'].includes(operator.toLowerCase())) {
            normalizedOperator = 'GREATER_THAN';
          } else if (['contains', 'includes'].includes(operator.toLowerCase())) {
            normalizedOperator = 'CONTAINS';
          } else if (['!=', 'not equals', 'is not'].includes(operator.toLowerCase())) {
            normalizedOperator = 'NOT_EQUALS';
          }
          
          // Determine logical operator based on position
          let logicalOperator: 'AND' | 'OR' | 'NOT' = 'AND';
          if (index > 0) {
            const conjunction = clauses[index - 1]?.toLowerCase();
            if (conjunction === 'or') {
              logicalOperator = 'OR';
            } else if (conjunction === 'but') {
              logicalOperator = 'NOT';
            }
          }
          
          conditions.push({
            attribute_path: attributePath.trim(),
            operator: normalizedOperator,
            value: value.trim().replace(/^["']|["']$/g, ''), // Remove quotes
            logical_operator: logicalOperator
          });
          break;
        }
      }
    });
    
    return { conditions, actions };
  };

  const handleNaturalLanguageParse = () => {
    if (!naturalLanguagePolicy.trim()) return;
    
    const { conditions, actions } = parseNaturalLanguagePolicy(naturalLanguagePolicy);
    setForm(prev => ({
      ...prev,
      conditions: conditions,
      actions: actions
    }));
    setShowNaturalLanguage(false);
  };

  const handleInputChange = (field: keyof PolicyForm, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addCondition = () => {
    const newCondition: PolicyCondition = {
      attribute_path: '',
      operator: 'EQUALS',
      value: '',
      logical_operator: 'AND'
    };
    setForm(prev => ({
      ...prev,
      conditions: [...prev.conditions, newCondition]
    }));
  };

  const updateCondition = (index: number, field: keyof PolicyCondition, value: any) => {
    setForm(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) =>
        i === index ? { ...condition, [field]: value } : condition
      )
    }));
  };

  const removeCondition = (index: number) => {
    setForm(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !form.tags.includes(newTag.trim())) {
      setForm(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addFramework = () => {
    if (newFramework.trim() && !form.compliance_frameworks.includes(newFramework.trim())) {
      setForm(prev => ({
        ...prev,
        compliance_frameworks: [...prev.compliance_frameworks, newFramework.trim()]
      }));
      setNewFramework('');
    }
  };

  const removeFramework = (frameworkToRemove: string) => {
    setForm(prev => ({
      ...prev,
      compliance_frameworks: prev.compliance_frameworks.filter(framework => framework !== frameworkToRemove)
    }));
  };

  const addAction = (actionName: string) => {
    if (actionName && !form.actions.includes(actionName)) {
      setForm(prev => ({
        ...prev,
        actions: [...prev.actions, actionName]
      }));
    }
  };

  const removeAction = (actionToRemove: string) => {
    setForm(prev => ({
      ...prev,
      actions: prev.actions.filter(action => action !== actionToRemove)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      alert(t('common.nameRequired'));
      return;
    }

    try {
      setSaving(true);
      const response = await abacPolicyService.create(form);
      
      if (response.success) {
        navigate('/abac/policies');
      }
    } catch (err) {
      console.error('Error creating policy:', err);
      alert(t('common.errorSavingData'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          onClick={() => navigate('/abac/policies')}
          className="flex items-center space-x-2"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          <span>{t('common.back')}</span>
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">
            {t('abac.policyCreate.title')}
          </h1>
          <p className="text-[var(--color-secondary-text)]">
            {t('abac.policyCreate.description')}
          </p>
        </div>
      </div>

             <form onSubmit={handleSubmit} className="space-y-6">
         {/* Basic Information */}
         <Card className="p-6">
           <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
             {t('abac.policyCreate.basicInformation')}
           </h2>
           
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
             <div>
               <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                 {t('common.name')} *
               </label>
               <input
                 type="text"
                 value={form.name}
                 onChange={(e) => handleInputChange('name', e.target.value)}
                 className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                 required
               />
             </div>

             <div>
               <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                 {t('common.status')}
               </label>
               <select
                 value={form.status}
                 onChange={(e) => handleInputChange('status', e.target.value)}
                 className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
               >
                 <option value="DRAFT">{t('common.draft')}</option>
                 <option value="ACTIVE">{t('common.active')}</option>
                 <option value="INACTIVE">{t('common.inactive')}</option>
               </select>
             </div>

             <div>
               <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                 {t('abac.policyCreate.priority')}
               </label>
               <input
                 type="number"
                 value={form.priority}
                 onChange={(e) => handleInputChange('priority', parseInt(e.target.value))}
                 min="1"
                 max="100"
                 className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
               />
             </div>

             <div>
               <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                 {t('abac.policyCreate.version')}
               </label>
               <input
                 type="text"
                 value={form.version}
                 onChange={(e) => handleInputChange('version', e.target.value)}
                 placeholder="1.0.0"
                 className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
               />
             </div>
           </div>

                       <div className="mt-4 grid grid-cols-6 gap-4">
              {/* Description - 50% (3 columns) */}
              <div className="col-span-3">
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                  {t('common.description')}
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                />
              </div>

              {/* Tags - 25% (1 column) */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                  {t('abac.policyCreate.tags')}
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.tags.map((tag, index) => (
                    <Badge key={index} color="blue" className="flex items-center space-x-1">
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 hover:text-red-600"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder={t('abac.policyCreate.addTag')}
                    className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                  />
                  <Button type="button" onClick={addTag} variant="outline">
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Compliance Frameworks - 25% (1 column) */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                  {t('abac.policyCreate.complianceFrameworks')}
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.compliance_frameworks.map((framework, index) => (
                    <Badge key={index} color="green" className="flex items-center space-x-1">
                      <span>{framework}</span>
                      <button
                        type="button"
                        onClick={() => removeFramework(framework)}
                        className="ml-1 hover:text-red-600"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newFramework}
                    onChange={(e) => setNewFramework(e.target.value)}
                    placeholder={t('abac.policyCreate.addFramework')}
                    className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                  />
                  <Button type="button" onClick={addFramework} variant="outline">
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
         </Card>

                 {/* Natural Language Policy Input */}
         {showNaturalLanguage && (
           <Card className="p-6">
             <div className="flex items-center space-x-2 mb-4">
               <SparklesIcon className="w-5 h-5 text-blue-600" />
               <h2 className="text-lg font-semibold text-[var(--color-secondary-text)]">
                 {t('abac.policyCreate.naturalLanguage')}
               </h2>
               <div className="relative">
                 <QuestionMarkCircleIcon 
                   className="w-5 h-5 text-gray-400 cursor-help"
                   onMouseEnter={() => setShowTooltip(true)}
                   onMouseLeave={() => setShowTooltip(false)}
                 />
                 {showTooltip && (
                   <div className="absolute z-10 w-80 p-3 bg-blue-50 border border-blue-200 rounded-md shadow-lg text-xs text-blue-700 -top-2 left-6">
                     <h3 className="font-medium mb-2">{t('abac.policyCreate.supportedPatterns')}:</h3>
                     <ul className="space-y-1">
                       <li>• <code>user.attribute = value</code> {t('abac.policyCreate.or')} <code>user.attribute equals value</code></li>
                       <li>• <code>resource.attribute = value</code> {t('abac.policyCreate.or')} <code>resource.attribute is value</code></li>
                       <li>• <code>environment.attribute = value</code></li>
                       <li>• <code>attribute &lt; value</code> {t('abac.policyCreate.or')} <code>attribute less than value</code></li>
                       <li>• <code>attribute &gt; value</code> {t('abac.policyCreate.or')} <code>attribute greater than value</code></li>
                       <li>• <code>attribute contains value</code></li>
                       <li>• <code>attribute != value</code> {t('abac.policyCreate.or')} <code>attribute is not value</code></li>
                       <li>• <code>action = read</code> {t('abac.policyCreate.or')} <code>allow action = write</code> {t('abac.policyCreate.or')} <code>then allow read</code></li>
                       <li>• {t('abac.policyCreate.useAndOrBut')} <code>and</code>, <code>or</code>, <code>but</code> {t('abac.policyCreate.toCombineConditions')}</li>
                     </ul>
                   </div>
                 )}
               </div>
             </div>
             
                            <div className="mb-4">
                 <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                   {t('abac.policyCreate.policyDescription')}
                 </label>
               <textarea
                 value={naturalLanguagePolicy}
                 onChange={(e) => setNaturalLanguagePolicy(e.target.value)}
                 placeholder={t('abac.policyCreate.examplePlaceholder')}
                 rows={4}
                 className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
               />
             </div>
             
             <div className="flex space-x-3">
               <Button
                 type="button"
                 onClick={handleNaturalLanguageParse}
                 disabled={!naturalLanguagePolicy.trim()}
                 className="flex items-center space-x-2"
               >
                 <SparklesIcon className="w-4 h-4" />
                 <span>{t('abac.policyCreate.parseAndConvert')}</span>
               </Button>
               <Button
                 type="button"
                 variant="outline"
                 onClick={() => setShowNaturalLanguage(false)}
               >
                 {t('abac.policyCreate.skipNaturalLanguage')}
               </Button>
             </div>
           </Card>
         )}

        

                 {/* Structured Policy */}
         <Card className="p-6">
           <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-[var(--color-secondary-text)]">
                 {t('abac.policyCreate.structuredPolicyDefinition')}
               </h2>
             <div className="flex space-x-2">
               {!showNaturalLanguage && (
                 <Button
                   type="button"
                   variant="outline"
                   onClick={() => setShowNaturalLanguage(true)}
                   className="flex items-center space-x-2"
                 >
                   <SparklesIcon className="w-4 h-4" />
                   <span>{t('abac.policyCreate.editInNaturalLanguage')}</span>
                 </Button>
               )}
             </div>
           </div>

                                   {/* Policy Effect and Actions */}
             <div className="mb-6 p-4 bg-gray-50 rounded-lg">
               <div className="flex items-center space-x-4 mb-4">
                 <div className="flex items-center space-x-2">
                   <span className="text-sm font-medium text-[var(--color-secondary-text)]">{t('abac.policyCreate.actionIs')}</span>
                   <select
                     value={form.effect}
                     onChange={(e) => handleInputChange('effect', e.target.value)}
                     className="w-24 px-3 py-1 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                   >
                     <option value="ALLOW">{t('common.allow')}</option>
                     <option value="DENY">{t('common.deny')}</option>
                   </select>
                   <span className="text-sm font-medium text-[var(--color-secondary-text)]">{t('abac.policyCreate.forTheseActions')}</span>
                 </div>
               </div>
               
               {/* Action Groups */}
               <div className="space-y-4">
                 {groupActions(availableActions).map((group, groupIndex) => (
                   <div key={groupIndex} className="border border-[var(--color-border)] rounded-md p-3">
                     <h4 className="text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                       {group.name}
                     </h4>
                     <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                       {group.actions.map((action: any) => (
                         <label key={action.id} className="flex items-center space-x-2 cursor-pointer">
                           <input
                             type="checkbox"
                             checked={form.actions.includes(action.action_name)}
                             onChange={(e) => {
                               if (e.target.checked) {
                                 addAction(action.action_name);
                               } else {
                                 removeAction(action.action_name);
                               }
                             }}
                             className="w-4 h-4 text-blue-600 border-[var(--color-border)] rounded focus:ring-blue-500"
                           />
                           <span className="text-sm text-[var(--color-secondary-text)]">
                             {action.action_name}
                           </span>
                         </label>
                       ))}
                     </div>
                   </div>
                 ))}
               </div>
               
               {/* Selected Actions Summary */}
               {form.actions.length > 0 && (
                 <div className="mt-4 pt-3 border-t border-[var(--color-border)]">
                   <h5 className="text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                     {t('abac.policyCreate.selectedActions')} ({form.actions.length}):
                   </h5>
                   <div className="flex flex-wrap gap-2">
                     {form.actions.map((action, index) => (
                       <Badge key={index} color="purple" className="flex items-center space-x-1">
                         <span>{action}</span>
                         <button
                           type="button"
                           onClick={() => removeAction(action)}
                           className="ml-1 hover:text-red-600"
                         >
                           <TrashIcon className="w-3 h-3" />
                         </button>
                       </Badge>
                     ))}
                   </div>
                 </div>
               )}
             </div>

           {/* Conditions Section */}
           <div>
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-md font-medium text-[var(--color-secondary-text)]">
                 {t('abac.policyCreate.whenUsersMeetConditions')}
               </h3>
               <Button type="button" onClick={addCondition} variant="outline" size="sm" className="flex items-center space-x-2">
                 <PlusIcon className="w-4 h-4" />
                 <span>{t('abac.policyCreate.addCondition')}</span>
               </Button>
             </div>

             {form.conditions.length === 0 ? (
               <p className="text-[var(--color-secondary-text)] text-center py-4 text-sm">
                 {t('abac.policyCreate.noConditionsDefined')}
               </p>
             ) : (
               <div className="space-y-3">
                 {form.conditions.map((condition, index) => (
                   <div key={index} className="border border-[var(--color-border)] rounded-md p-3">
                     <div className="flex justify-between items-center mb-2">
                       <span className="text-sm font-medium text-[var(--color-secondary-text)]">
                         {t('abac.policyCreate.condition')} {index + 1}
                       </span>
                       <Button
                         type="button"
                         onClick={() => removeCondition(index)}
                         variant="outline"
                         size="sm"
                         className="text-red-600 hover:text-red-700"
                       >
                         <TrashIcon className="w-3 h-3" />
                       </Button>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
                                               <div>
                                                     <label className="block text-xs font-medium text-[var(--color-secondary-text)] mb-1">
                             {t('abac.policyCreate.attributePath')}
                           </label>
                          <select
                            value={condition.attribute_path}
                            onChange={(e) => updateCondition(index, 'attribute_path', e.target.value)}
                            className="w-full px-2 py-1 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] text-sm"
                          >
                                                         <option value="">{t('abac.policyCreate.selectAttribute')}</option>
                            {groupAttributes(availableAttributes).map((group) => (
                              <optgroup key={group.name} label={group.name}>
                                {group.attributes.map((attr: any) => (
                                  <option key={attr.id} value={attr.attribute_path}>
                                    {attr.attribute_name}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        </div>

                        <div>
                                                     <label className="block text-xs font-medium text-[var(--color-secondary-text)] mb-1">
                             {t('abac.policyCreate.operator')}
                           </label>
                          <select
                            value={condition.operator}
                            onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                            className="w-32 px-2 py-1 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] text-sm"
                          >
                                                         <option value="EQUALS">{t('abac.policyCreate.equals')}</option>
                             <option value="NOT_EQUALS">{t('abac.policyCreate.notEquals')}</option>
                             <option value="CONTAINS">{t('abac.policyCreate.contains')}</option>
                             <option value="GREATER_THAN">{t('abac.policyCreate.greaterThan')}</option>
                             <option value="LESS_THAN">{t('abac.policyCreate.lessThan')}</option>
                             <option value="IN">{t('abac.policyCreate.in')}</option>
                             <option value="NOT_IN">{t('abac.policyCreate.notIn')}</option>
                          </select>
                        </div>

                       <div>
                         <label className="block text-xs font-medium text-[var(--color-secondary-text)] mb-1">
                           {t('abac.policyCreate.value')}
                         </label>
                         <input
                           type="text"
                           value={condition.value}
                           onChange={(e) => updateCondition(index, 'value', e.target.value)}
                           className="w-full px-2 py-1 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] text-sm"
                         />
                       </div>

                                               <div>
                                                     <label className="block text-xs font-medium text-[var(--color-secondary-text)] mb-1">
                             {t('abac.policyCreate.logicalOperator')}
                           </label>
                          <select
                            value={condition.logical_operator}
                            onChange={(e) => updateCondition(index, 'logical_operator', e.target.value)}
                            className="w-20 px-2 py-1 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] text-sm"
                          >
                                                         <option value="AND">{t('abac.policyCreate.and')}</option>
                             <option value="OR">{t('abac.policyCreate.or')}</option>
                             <option value="NOT">{t('abac.policyCreate.not')}</option>
                          </select>
                        </div>
                     </div>
                   </div>
                 ))}
               </div>
             )}
           </div>
         </Card>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/abac/policies')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2"
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
};
